import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { chatAPI, authAPI } from '../services/api';
import { Loading, EmptyState, ConfirmDialog } from '../components/common';
import Modal from '../components/common/Modal';
import { formatRelativeTime, formatFileSize } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  UserPlusIcon,
  MagnifyingGlassIcon,
  PaperClipIcon,
  XMarkIcon,
  DocumentIcon,
  PhotoIcon,
  ArrowPathIcon,
  UserGroupIcon,
  PlusIcon,
  Cog6ToothIcon,
  UserMinusIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';

const Chat = () => {
  const { user } = useSelector((state) => state.auth);
  
  // State
  const [rooms, setRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  
  // UI State
  const [messageText, setMessageText] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [showGroupSettingsModal, setShowGroupSettingsModal] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  const [searchRoom, setSearchRoom] = useState('');
  const [searchAddMember, setSearchAddMember] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  
  // Group chat form
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [newMembers, setNewMembers] = useState([]);
  
  // Refs
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Fetch rooms
  const fetchRooms = useCallback(async () => {
    try {
      const response = await chatAPI.getRooms();
      setRooms(response.data.results || response.data || []);
    } catch (error) {
      console.error('Rooms fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch messages
  const fetchMessages = useCallback(async (roomId) => {
    if (!roomId) return;
    setMessagesLoading(true);
    try {
      const response = await chatAPI.getMessages(roomId);
      const msgs = response.data.results || response.data || [];
      // Debug: log first message structure
      if (msgs.length > 0) {
        console.log('Message structure:', JSON.stringify(msgs[0], null, 2));
      }
      setMessages(Array.isArray(msgs) ? msgs : []);
      // Mark as read
      await chatAPI.markAsRead(roomId).catch(() => {});
    } catch (error) {
      console.error('Messages fetch error:', error);
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const response = await authAPI.getUsers();
      setUsers(response.data.results || response.data || []);
    } catch (error) {
      console.error('Users fetch error:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchRooms();
    fetchUsers();
  }, [fetchRooms, fetchUsers]);

  // Load messages when room changes
  useEffect(() => {
    if (currentRoom) {
      fetchMessages(currentRoom.id);
      
      // Poll for new messages every 5 seconds
      pollIntervalRef.current = setInterval(() => {
        fetchMessages(currentRoom.id);
      }, 5000);
      
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [currentRoom, fetchMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('Fayl hajmi 10MB dan oshmasligi kerak');
        return;
      }
      setSelectedFile(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  // Clear file
  const clearFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Send message
  const handleSend = async (e) => {
    e.preventDefault();
    if ((!messageText.trim() && !selectedFile) || !currentRoom || sending) return;
    
    setSending(true);
    try {
      if (selectedFile) {
        // Send with file
        const formData = new FormData();
        if (messageText.trim()) {
          formData.append('text', messageText.trim());
        }
        formData.append('file', selectedFile);
        formData.append('room', currentRoom.id);
        await chatAPI.sendMessageWithFile(currentRoom.id, formData);
      } else {
        // Send text only
        await chatAPI.sendMessage(currentRoom.id, { 
          text: messageText.trim(),
          room: currentRoom.id 
        });
      }
      
      setMessageText('');
      clearFile();
      fetchMessages(currentRoom.id);
      fetchRooms(); // Update room list for last message
    } catch (error) {
      console.error('Send error:', error);
      toast.error('Xabar yuborishda xatolik');
    } finally {
      setSending(false);
    }
  };

  // Start private chat
  const handleStartPrivateChat = async (userId) => {
    try {
      const response = await chatAPI.startPrivateChat({ user_id: userId });
      const room = response.data;
      setShowNewChatModal(false);
      setSearchUser('');
      await fetchRooms();
      setCurrentRoom(room);
    } catch (error) {
      console.error('Start chat error:', error);
      toast.error('Chat boshlashda xatolik');
    }
  };

  // Create group chat
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!groupName.trim() || selectedMembers.length === 0) {
      toast.error('Guruh nomi va a\'zolarni tanlang');
      return;
    }
    
    try {
      const response = await chatAPI.createRoom({
        name: groupName,
        room_type: 'group',
        participants: selectedMembers,
      });
      setShowGroupModal(false);
      setGroupName('');
      setSelectedMembers([]);
      await fetchRooms();
      setCurrentRoom(response.data);
      toast.success('Guruh yaratildi!');
    } catch (error) {
      console.error('Create group error:', error);
      toast.error('Guruh yaratishda xatolik');
    }
  };

  // Add members to group
  const handleAddMembers = async () => {
    if (newMembers.length === 0) {
      toast.error('A\'zolarni tanlang');
      return;
    }
    
    try {
      const response = await chatAPI.addParticipants(currentRoom.id, newMembers);
      toast.success(response.data.message);
      setShowAddMembersModal(false);
      setNewMembers([]);
      setSearchAddMember('');
      // Refresh room data
      const updatedRoom = response.data.room;
      setCurrentRoom(updatedRoom);
      fetchRooms();
    } catch (error) {
      console.error('Add members error:', error);
      toast.error('A\'zo qo\'shishda xatolik');
    }
  };

  // Remove member from group
  const handleRemoveMember = async (userId) => {
    try {
      const response = await chatAPI.removeParticipant(currentRoom.id, userId);
      toast.success(response.data.message);
      // Refresh room data
      if (response.data.room) {
        setCurrentRoom(response.data.room);
      }
      fetchRooms();
    } catch (error) {
      console.error('Remove member error:', error);
      toast.error(error.response?.data?.error || 'A\'zo o\'chirishda xatolik');
    }
  };

  // Leave group
  const handleLeaveGroup = async () => {
    try {
      await chatAPI.leaveRoom(currentRoom.id);
      toast.success('Guruhdan chiqdingiz');
      setShowLeaveConfirm(false);
      setCurrentRoom(null);
      fetchRooms();
    } catch (error) {
      console.error('Leave group error:', error);
      toast.error('Guruhdan chiqishda xatolik');
    }
  };

  // Toggle new member selection
  const toggleNewMember = (userId) => {
    setNewMembers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Get users not in current room
  const getAvailableUsers = () => {
    if (!currentRoom) return [];
    const participantIds = currentRoom.participants || [];
    return users.filter(u => 
      !participantIds.includes(u.id) && 
      u.id !== user?.id &&
      (searchAddMember === '' || 
       u.first_name?.toLowerCase().includes(searchAddMember.toLowerCase()) ||
       u.last_name?.toLowerCase().includes(searchAddMember.toLowerCase()) ||
       u.username.toLowerCase().includes(searchAddMember.toLowerCase()))
    );
  };

  // Toggle member selection
  const toggleMember = (userId) => {
    setSelectedMembers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Filter users
  const filteredUsers = users.filter(u => 
    u.id !== user?.id && 
    (u.first_name?.toLowerCase().includes(searchUser.toLowerCase()) || 
     u.last_name?.toLowerCase().includes(searchUser.toLowerCase()) ||
     u.username.toLowerCase().includes(searchUser.toLowerCase()))
  );

  // Get room display name
  const getRoomName = (room) => {
    // Shaxsiy chat uchun other_user dan ism olish
    if (room.room_type === 'private' && room.other_user) {
      return room.other_user.full_name || 
             `${room.other_user.first_name || ''} ${room.other_user.last_name || ''}`.trim() ||
             room.other_user.username;
    }
    // Guruh chat uchun nom
    if (room.name) return room.name;
    return `Chat #${room.id}`;
  };

  // Get sender name
  const getSenderName = (msg) => {
    // Direct sender_name field
    if (msg.sender_name) return msg.sender_name;
    
    // sender_details object (common in DRF)
    if (msg.sender_details) {
      const s = msg.sender_details;
      if (s.first_name || s.last_name) {
        return `${s.first_name || ''} ${s.last_name || ''}`.trim();
      }
      return s.username || s.email || 'Foydalanuvchi';
    }
    
    // sender as object
    if (msg.sender && typeof msg.sender === 'object') {
      const s = msg.sender;
      if (s.first_name || s.last_name) {
        return `${s.first_name || ''} ${s.last_name || ''}`.trim();
      }
      return s.username || s.email || 'Foydalanuvchi';
    }
    
    // user object (alternative field name)
    if (msg.user && typeof msg.user === 'object') {
      const u = msg.user;
      if (u.first_name || u.last_name) {
        return `${u.first_name || ''} ${u.last_name || ''}`.trim();
      }
      return u.username || 'Foydalanuvchi';
    }
    
    // author object (alternative field name)
    if (msg.author && typeof msg.author === 'object') {
      const a = msg.author;
      if (a.first_name || a.last_name) {
        return `${a.first_name || ''} ${a.last_name || ''}`.trim();
      }
      return a.username || 'Foydalanuvchi';
    }
    
    // Find user from users list by sender_id or sender (as ID)
    const senderId = msg.sender_id || (typeof msg.sender === 'number' ? msg.sender : null) || msg.user_id;
    if (senderId) {
      const senderUser = users.find(u => u.id === senderId);
      if (senderUser) {
        if (senderUser.first_name || senderUser.last_name) {
          return `${senderUser.first_name || ''} ${senderUser.last_name || ''}`.trim();
        }
        return senderUser.username || 'Foydalanuvchi';
      }
    }
    
    return 'Foydalanuvchi';
  };

  // Filter rooms by search
  const filteredRooms = rooms.filter(room => {
    if (!searchRoom.trim()) return true;
    const roomName = getRoomName(room).toLowerCase();
    return roomName.includes(searchRoom.toLowerCase());
  });

  // Get room avatar
  const getRoomAvatar = (room) => {
    if (room.room_type === 'private' && room.other_user) {
      return room.other_user.first_name?.[0] || room.other_user.username[0];
    }
    return room.name?.[0] || 'G';
  };

  // Check if message is from current user
  const isOwnMessage = (msg) => {
    return msg.sender === user?.id || msg.sender_id === user?.id || msg.sender?.id === user?.id;
  };

  // Get file icon
  const getFileIcon = (fileName) => {
    if (!fileName) return DocumentIcon;
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return PhotoIcon;
    return DocumentIcon;
  };

  if (loading) return <Loading />;

  return (
    <div className="h-[calc(100vh-180px)] flex gap-4">
      {/* Rooms List */}
      <div className="w-80 bg-white rounded-xl shadow-sm border overflow-hidden flex flex-col">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-800">Suhbatlar</h2>
            <div className="flex gap-1">
              <button 
                onClick={() => setShowNewChatModal(true)} 
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Yangi chat"
              >
                <UserPlusIcon className="w-5 h-5 text-gray-600" />
              </button>
              <button 
                onClick={() => setShowGroupModal(true)} 
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Guruh yaratish"
              >
                <UserGroupIcon className="w-5 h-5 text-gray-600" />
              </button>
              <button 
                onClick={fetchRooms} 
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Yangilash"
              >
                <ArrowPathIcon className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
          {/* Search rooms */}
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Suhbatlarni qidirish..."
              className="input pl-9 py-2 text-sm"
              value={searchRoom}
              onChange={(e) => setSearchRoom(e.target.value)}
            />
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {filteredRooms.length > 0 ? filteredRooms.map((room) => (
            <button
              key={room.id}
              onClick={() => setCurrentRoom(room)}
              className={`w-full p-4 flex items-center gap-3 hover:bg-gray-50 border-b transition-colors ${
                currentRoom?.id === room.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold ${
                room.room_type === 'group' ? 'bg-green-500' : 'bg-blue-500'
              }`}>
                {getRoomAvatar(room)}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="font-medium text-gray-800 truncate">{getRoomName(room)}</p>
                {room.last_message && (
                  <p className="text-sm text-gray-500 truncate">
                    {room.last_message.text || (room.last_message.file ? '📎 Fayl' : '')}
                  </p>
                )}
              </div>
              {room.unread_count > 0 && (
                <span className="w-6 h-6 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                  {room.unread_count > 9 ? '9+' : room.unread_count}
                </span>
              )}
            </button>
          )) : (
            <div className="p-4 text-center text-gray-500">
              <ChatBubbleLeftRightIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>Suhbatlar yo'q</p>
              <button 
                onClick={() => setShowNewChatModal(true)}
                className="mt-2 text-blue-600 text-sm hover:underline"
              >
                Yangi chat boshlash
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 bg-white rounded-xl shadow-sm border overflow-hidden flex flex-col">
        {currentRoom ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                  currentRoom.room_type === 'group' ? 'bg-green-500' : 'bg-blue-500'
                }`}>
                  {getRoomAvatar(currentRoom)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">{getRoomName(currentRoom)}</h3>
                  <p className="text-sm text-gray-500">
                    {currentRoom.room_type === 'group' 
                      ? `${currentRoom.participants_count || currentRoom.participants?.length || 0} ta a'zo`
                      : 'Shaxsiy chat'
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {currentRoom.room_type === 'group' && (
                  <>
                    <button 
                      onClick={() => setShowAddMembersModal(true)} 
                      className="p-2 hover:bg-gray-100 rounded-lg"
                      title="A'zo qo'shish"
                    >
                      <UserPlusIcon className="w-5 h-5 text-gray-600" />
                    </button>
                    <button 
                      onClick={() => setShowGroupSettingsModal(true)} 
                      className="p-2 hover:bg-gray-100 rounded-lg"
                      title="Guruh sozlamalari"
                    >
                      <Cog6ToothIcon className="w-5 h-5 text-gray-600" />
                    </button>
                  </>
                )}
                <button onClick={() => fetchMessages(currentRoom.id)} className="p-2 hover:bg-gray-100 rounded-lg" title="Yangilash">
                  <ArrowPathIcon className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messagesLoading && messages.length === 0 ? (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              ) : messages.length > 0 ? (
                messages.map((msg, idx) => {
                  const own = isOwnMessage(msg);
                  const FileIcon = getFileIcon(msg.file_name);
                  const senderName = getSenderName(msg);
                  
                  return (
                    <div key={msg.id || idx} className={`flex ${own ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] ${own ? 'bg-blue-600 text-white' : 'bg-white shadow-sm'} rounded-2xl px-4 py-2`}>
                        {/* Sender name - always show for non-own messages */}
                        {!own && (
                          <p className="text-xs font-semibold mb-1 text-blue-600">
                            {senderName}
                          </p>
                        )}
                        
                        {/* File attachment */}
                        {(msg.file || msg.attachment) && (
                          <a 
                            href={msg.file || msg.attachment} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`flex items-center gap-2 p-2 rounded-lg mb-2 ${
                              own ? 'bg-blue-500 hover:bg-blue-400' : 'bg-gray-100 hover:bg-gray-200'
                            }`}
                          >
                            {msg.file_name?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                              <img 
                                src={msg.file || msg.attachment} 
                                alt="Rasm" 
                                className="max-w-full max-h-48 rounded-lg"
                              />
                            ) : (
                              <>
                                <FileIcon className={`w-8 h-8 ${own ? 'text-white' : 'text-gray-600'}`} />
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium truncate ${own ? 'text-white' : 'text-gray-800'}`}>
                                    {msg.file_name || 'Fayl'}
                                  </p>
                                  {msg.file_size && (
                                    <p className={`text-xs ${own ? 'text-blue-200' : 'text-gray-500'}`}>
                                      {formatFileSize(msg.file_size)}
                                    </p>
                                  )}
                                </div>
                              </>
                            )}
                          </a>
                        )}
                        
                        {/* Message text */}
                        {msg.text && <p className="break-words">{msg.text}</p>}
                        
                        {/* Timestamp */}
                        <p className={`text-xs mt-1 ${own ? 'text-blue-200' : 'text-gray-400'}`}>
                          {formatRelativeTime(msg.created_at || msg.timestamp)}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <ChatBubbleLeftRightIcon className="w-16 h-16 mb-2 text-gray-300" />
                  <p>Xabarlar yo'q</p>
                  <p className="text-sm">Birinchi xabarni yuboring!</p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            {/* File Preview */}
            {selectedFile && (
              <div className="px-4 py-2 border-t bg-gray-50">
                <div className="flex items-center gap-3 p-2 bg-white rounded-lg border">
                  {filePreview ? (
                    <img src={filePreview} alt="Preview" className="w-12 h-12 object-cover rounded" />
                  ) : (
                    <DocumentIcon className="w-12 h-12 text-gray-400" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{selectedFile.name}</p>
                    <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
                  </div>
                  <button onClick={clearFile} className="p-1 hover:bg-gray-100 rounded">
                    <XMarkIcon className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>
            )}
            
            {/* Message Input */}
            <form onSubmit={handleSend} className="p-4 border-t flex gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
                title="Fayl biriktirish"
              >
                <PaperClipIcon className="w-6 h-6" />
              </button>
              <input
                type="text"
                className="input flex-1"
                placeholder="Xabar yozing..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
              />
              <button
                type="submit"
                disabled={(!messageText.trim() && !selectedFile) || sending}
                className="btn-primary px-4 disabled:opacity-50"
              >
                {sending ? (
                  <ArrowPathIcon className="w-5 h-5 animate-spin" />
                ) : (
                  <PaperAirplaneIcon className="w-5 h-5" />
                )}
              </button>
            </form>
          </>
        ) : (
          <EmptyState 
            icon={ChatBubbleLeftRightIcon} 
            title="Suhbatni tanlang" 
            description="Chap paneldan suhbatni tanlang yoki yangi suhbat boshlang"
            action={
              <button onClick={() => setShowNewChatModal(true)} className="btn-primary mt-4">
                <PlusIcon className="w-5 h-5" /> Yangi chat
              </button>
            }
          />
        )}
      </div>

      {/* New Chat Modal */}
      <Modal isOpen={showNewChatModal} onClose={() => setShowNewChatModal(false)} title="Yangi chat boshlash" size="md">
        <div className="space-y-4">
          <div className="relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Foydalanuvchi qidirish..."
              className="input pl-10"
              value={searchUser}
              onChange={(e) => setSearchUser(e.target.value)}
            />
          </div>
          
          <div className="max-h-80 overflow-y-auto space-y-1">
            {filteredUsers.length > 0 ? filteredUsers.map((u) => (
              <button
                key={u.id}
                onClick={() => handleStartPrivateChat(u.id)}
                className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg text-left"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                  {u.first_name?.[0] || u.username[0].toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-gray-800">
                    {u.first_name} {u.last_name || u.username}
                  </p>
                  <p className="text-sm text-gray-500">@{u.username}</p>
                </div>
              </button>
            )) : (
              <p className="text-center text-gray-500 py-4">Foydalanuvchilar topilmadi</p>
            )}
          </div>
        </div>
      </Modal>

      {/* Create Group Modal */}
      <Modal isOpen={showGroupModal} onClose={() => setShowGroupModal(false)} title="Guruh yaratish" size="md">
        <form onSubmit={handleCreateGroup} className="space-y-4">
          <div className="input-group">
            <label className="label">Guruh nomi *</label>
            <input
              type="text"
              className="input"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Masalan: Moliya bo'limi"
              required
            />
          </div>
          
          <div className="input-group">
            <label className="label">A'zolarni tanlang * ({selectedMembers.length} ta tanlangan)</label>
            <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
              {users.filter(u => u.id !== user?.id).map((u) => (
                <label
                  key={u.id}
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedMembers.includes(u.id)}
                    onChange={() => toggleMember(u.id)}
                    className="w-4 h-4"
                  />
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">
                    {u.first_name?.[0] || u.username[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 text-sm">
                      {u.first_name} {u.last_name || u.username}
                    </p>
                    <p className="text-xs text-gray-500">{u.organization_name || u.username}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => setShowGroupModal(false)} className="btn-secondary">
              Bekor qilish
            </button>
            <button type="submit" className="btn-primary" disabled={!groupName.trim() || selectedMembers.length === 0}>
              Yaratish
            </button>
          </div>
        </form>
      </Modal>

      {/* Add Members Modal */}
      <Modal isOpen={showAddMembersModal} onClose={() => { setShowAddMembersModal(false); setNewMembers([]); setSearchAddMember(''); }} title="A'zo qo'shish" size="md">
        <div className="space-y-4">
          <div className="relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Foydalanuvchi qidirish..."
              className="input pl-10"
              value={searchAddMember}
              onChange={(e) => setSearchAddMember(e.target.value)}
            />
          </div>
          
          <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
            {getAvailableUsers().length > 0 ? getAvailableUsers().map((u) => (
              <label
                key={u.id}
                className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={newMembers.includes(u.id)}
                  onChange={() => toggleNewMember(u.id)}
                  className="w-4 h-4"
                />
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">
                  {u.first_name?.[0] || u.username[0].toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-gray-800 text-sm">
                    {u.first_name || ''} {u.last_name || u.username}
                  </p>
                  <p className="text-xs text-gray-500">@{u.username}</p>
                </div>
              </label>
            )) : (
              <p className="text-center text-gray-500 py-4">Qo'shiladigan foydalanuvchi yo'q</p>
            )}
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => { setShowAddMembersModal(false); setNewMembers([]); }} className="btn-secondary">
              Bekor qilish
            </button>
            <button onClick={handleAddMembers} className="btn-primary" disabled={newMembers.length === 0}>
              Qo'shish ({newMembers.length})
            </button>
          </div>
        </div>
      </Modal>

      {/* Group Settings Modal */}
      <Modal isOpen={showGroupSettingsModal} onClose={() => setShowGroupSettingsModal(false)} title="Guruh sozlamalari" size="md">
        <div className="space-y-4">
          <div className="input-group">
            <label className="label">Guruh nomi</label>
            <p className="font-medium">{currentRoom?.name || 'Nomsiz guruh'}</p>
          </div>
          
          <div>
            <label className="label">A'zolar ({currentRoom?.participants_details?.length || 0})</label>
            <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
              {currentRoom?.participants_details?.map((p) => (
                <div key={p.id} className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">
                      {p.first_name?.[0] || p.username[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">
                        {p.first_name || ''} {p.last_name || p.username}
                        {p.id === user?.id && <span className="text-gray-400 ml-1">(Siz)</span>}
                        {currentRoom?.created_by === p.id && <span className="text-blue-600 ml-1">(Admin)</span>}
                      </p>
                      <p className="text-xs text-gray-500">@{p.username}</p>
                    </div>
                  </div>
                  {currentRoom?.created_by === user?.id && p.id !== user?.id && (
                    <button 
                      onClick={() => handleRemoveMember(p.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                      title="O'chirish"
                    >
                      <UserMinusIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-between pt-4 border-t">
            <button 
              onClick={() => { setShowGroupSettingsModal(false); setShowLeaveConfirm(true); }}
              className="btn-secondary text-red-600 hover:bg-red-50"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              Guruhdan chiqish
            </button>
            <button onClick={() => setShowGroupSettingsModal(false)} className="btn-primary">
              Yopish
            </button>
          </div>
        </div>
      </Modal>

      {/* Leave Group Confirm */}
      <ConfirmDialog
        isOpen={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        onConfirm={handleLeaveGroup}
        title="Guruhdan chiqish"
        message={`"${currentRoom?.name}" guruhidan chiqishni xohlaysizmi?`}
        confirmText="Chiqish"
      />
    </div>
  );
};

export default Chat;
